import { ArrowDownIcon, ArrowUpIcon } from '@radix-ui/react-icons';
import type { Column } from '@tanstack/react-table';
import { useState, type ReactElement, type ReactNode } from 'react';

import { SortCarats } from '../../../assets/icons/sort-carats.tsx';
import { Button } from '../../../components/ui/button.tsx';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { cn } from '../../../lib/utils.ts';

type SortableColumn = Pick<Column<unknown>, 'getCanSort' | 'getIsSorted' | 'toggleSorting'>;

export type DataTableSortHeaderProps = {
    column: SortableColumn;
    title: ReactNode;
    align?: 'left' | 'center' | 'right';
    className?: string;
    /** Rendered between the label and the sort indicator, outside the trigger button. */
    children?: ReactNode;
};

export function DataTableSortHeader({
    align = 'left',
    children,
    className,
    column,
    title,
}: DataTableSortHeaderProps): ReactElement {
    const { t } = useLocale();
    const [open, setOpen] = useState(false);

    if (!column.getCanSort()) {
        return (
            <div className={cn('flex items-center gap-tight', className)}>
                {title}
                {children}
            </div>
        );
    }

    const isSelected = column.getIsSorted();
    const isHighlighted = open || isSelected;

    const indicator =
        column.getIsSorted() === 'desc' ? (
            <ArrowDownIcon
                className={cn('h-3 w-3', isHighlighted ? 'stroke-neutral-800' : 'stroke-neutral-300')}
            />
        ) : column.getIsSorted() === 'asc' ? (
            <ArrowUpIcon
                className={cn('h-3 w-3', isHighlighted ? 'stroke-neutral-800' : 'stroke-neutral-300')}
            />
        ) : (
            <SortCarats
                className={cn(
                    'h-2.5 w-2.5',
                    isHighlighted
                        ? 'fill-neutral-800 stroke-neutral-800'
                        : 'fill-neutral-300 stroke-neutral-300',
                )}
            />
        );

    return (
        <DropdownMenu onOpenChange={setOpen}>
            <div
                className={cn(
                    'flex items-center',
                    align === 'left' && '-ml-2 justify-start text-left',
                    align === 'center' && 'justify-center text-center',
                    align === 'right' && '-mr-2 justify-end text-right',
                    className,
                )}
            >
                <div
                    className={cn(
                        'inline-flex items-center rounded-md hover:bg-neutral-100',
                        open && 'bg-accent',
                    )}
                >
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            size="sm"
                            className={cn(
                                'items-center bg-transparent outline-hidden! hover:bg-transparent active:bg-transparent focus:border-transparent! focus:ring-0!',
                                isHighlighted ? 'text-neutral-800' : 'text-neutral-600',
                            )}
                        >
                            <span>{title}</span>
                        </Button>
                    </DropdownMenuTrigger>
                    {children}
                    <DropdownMenuTrigger asChild>
                        <button
                            aria-hidden
                            className="flex h-8 cursor-pointer items-center pr-2 pl-1"
                            id={undefined}
                            tabIndex={-1}
                            type="button"
                        >
                            {indicator}
                        </button>
                    </DropdownMenuTrigger>
                </div>
            </div>
            <DropdownMenuContent align="end">
                <DropdownMenuItem
                    onClick={() => {
                        column.toggleSorting(false);
                    }}
                >
                    <ArrowUpIcon className="mr-2 h-4 w-4 fill-neutral-800 stroke-neutral-800 text-neutral-800" />
                    {t('ascending')}
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={() => {
                        column.toggleSorting(true);
                    }}
                >
                    <ArrowDownIcon className=" mr-2 h-4 w-4 fill-neutral-800 stroke-neutral-800 text-neutral-800" />
                    {t('descending')}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
